import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import {
  Moon, Sun, User, LogOut, Settings,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { useAuthStore } from '@/stores/authStore';
import { useLogout } from '@/lib/hooks/useLogout';
import { useThemeStore } from '@/stores/themeStore';
import { cn } from '@/lib/cn';
import { Avatar } from '@/features/Profile/Avatar';

interface NavItem {
  to: string;
  label: string;
  roles: string[];
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Explore', roles: ['student', 'super_admin'] },
  { to: '/problems', label: 'Problems', roles: ['student', 'super_admin'] },
  // { to: '/contests', label: 'Contests', roles: ['student', 'super_admin'] },
  { to: '/admin/orgs', label: 'Admin', roles: ['super_admin'] },
  { to: '/admin/curriculum', label: 'Curriculum', roles: ['super_admin'] },
];

export function AppNavbar() {
  const { user } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = user?.role;

  const visibleItems = navItems.filter((item) =>
    userRole && item.roles.includes(userRole)
  );

  const handleLogout = useLogout();

  return (
    <header className="flex h-14 items-center border-b border-border px-6">
      {/* Logo */}
      <button
        onClick={() => navigate({ to: '/dashboard' })}
        className="mr-8 flex-shrink-0 h-10 overflow-hidden"
      >
        <img
          src="/images/logo.svg"
          alt="KodPrep"
          className="h-8 w-auto"
        />
      </button>

      {/* Navigation tabs */}
      <nav className="flex items-center gap-1">
        {visibleItems.map(({ to, label }) => {
          const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
          return (
            <button
              key={to}
              onClick={() => navigate({ to: to as any })}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                isActive
                  ? 'text-foreground bg-muted'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
              )}
            >
              {label}
            </button>
          );
        })}
      </nav>

      {/* Right side: theme toggle + user menu */}
      <div className="ml-auto flex items-center gap-3">
        <button onClick={toggleTheme} className="rounded p-1.5 hover:bg-muted">
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger className="flex items-center gap-2 rounded-full hover:ring-2 hover:ring-border transition-all">
            <Avatar src={user?.avatarUrl} name={user?.username} size={32} />
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-50 w-[280px] rounded-xl border border-border bg-background shadow-xl"
              sideOffset={8}
              align="end"
            >
              {/* User header */}
              <div className="flex items-center gap-3 p-4 pb-3">
                <Avatar src={user?.avatarUrl} name={user?.username} size={48} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{user?.username}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>

              <DropdownMenu.Separator className="h-px bg-border" />

              {/* Menu items */}
              <div className="p-1.5">
                <DropdownMenu.Item className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-muted outline-none" asChild>
                  <Link to="/profile">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-muted outline-none" asChild>
                  <Link to="/settings">
                    <Settings className="h-4 w-4 text-muted-foreground" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenu.Item>
              </div>

              <DropdownMenu.Separator className="h-px bg-border" />

              {/* Sign out */}
              <div className="p-1.5">
                <DropdownMenu.Item
                  onClick={handleLogout}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-destructive hover:bg-destructive/10 outline-none"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenu.Item>
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
